import type { Control, UseFormReturn } from 'react-hook-form';
import { Controller, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import type { MetricEventUrlKind } from '@gonfalon/metrics/types';
import {
  Alert,
  Button,
  FieldError,
  FieldGroup,
  IconButton,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
  TextField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import {
  allowDecision,
  isAbsoluteUrl,
  type MetricUrlAccessDecision,
  URL_MATCHER_KINDS,
  urlMatcherKindDisplayName,
} from '../../internal/CreateMetricModal/metricUrlUtils';
import type { MetricForm } from '../formUtils';

import { RequiredLabel } from './RequiredLabel';

import styles from './MetricUrls.module.css';

const FORM_HINT_IDS = {
  canonical: 'metrics.targetUrls.simpleMatchHint',
  exact: 'metrics.targetUrls.exactMatchHint',
  substring: 'metrics.targetUrls.substringMatchHint',
  regex: 'metrics.targetUrls.regularExpressionHint',
} as const satisfies Record<MetricEventUrlKind, string>;

const formHint = (kind: MetricEventUrlKind) => msg(FORM_HINT_IDS[kind]);

type Props = {
  access?: MetricUrlAccessDecision;
  isSaving?: boolean;
  context: UseFormReturn<MetricForm>;
};

export function MetricUrls({ access = allowDecision(), isSaving, context }: Props) {
  const { control, watch } = context;
  const { fields, append, remove } = useFieldArray({ control, name: 'urls' });
  const { errors } = useFormState<MetricForm>({ control, name: 'urls' });
  const watchArr = watch('urls', fields);

  return (
    <div className={styles.targetUrlContainer}>
      {fields.map(({ id }, index) => (
        <div key={id} className={styles.targetUrlRow}>
          <FieldGroup aria-label={msg('metrics.targetUrls.urlGroupLabel', { number: index + 1 })} name="urls">
            <div>
              <div className={styles.targetingRow}>
                <div className={styles.targetType}>
                  <Controller
                    control={control}
                    name={`urls.${index}.kind`}
                    render={({ field }) => (
                      <Select
                        onSelectionChange={field.onChange}
                        selectedKey={field.value}
                        isDisabled={isSaving || !access.isAllowed}
                        aria-invalid={errors.urls?.[index] ? 'true' : 'false'}
                      >
                        <RequiredLabel
                          label={msg('metrics.targetUrls.typeLabel')}
                          htmlFor="targetType"
                          className={styles.targetLabel}
                        />
                        <Button>
                          <SelectValue />
                          <Icon name="chevron-down" size="small" />
                        </Button>
                        <Popover>
                          <ListBox>
                            {URL_MATCHER_KINDS.map((value) => (
                              <ListBoxItem key={value} id={value}>
                                {urlMatcherKindDisplayName(value)}
                              </ListBoxItem>
                            ))}
                          </ListBox>
                        </Popover>
                      </Select>
                    )}
                  />
                </div>
                <TextField className={styles.targetUrlInput} isInvalid={Boolean(errors.urls?.[index])}>
                  <RequiredLabel label={msg('metrics.targetUrls.urlLabel')} className={styles.targetLabel} />

                  <div className={styles.removeTargetUrlContainer}>
                    <Input
                      key={id}
                      {...control.register(`urls.${index}.value`, {
                        validate: (value, formValues) => validate(value, formValues, index),
                        deps: ['kind'],
                      })}
                      value={watchArr && watchArr[index].value}
                      disabled={isSaving || !access.isAllowed}
                      placeholder={msg('metrics.targetUrls.urlPlaceholder')}
                      className={styles.targetUrlInputBar}
                    />
                    {fields.length > 1 && (
                      <IconButton
                        aria-label={msg('metrics.targetUrls.removeUrlLabel', { number: index + 1 })}
                        icon="minus-circle"
                        onPress={() => remove(index)}
                        className={styles.removeTargetUrlIcon}
                      />
                    )}
                  </div>
                  {errors.urls?.[index] && (
                    <FieldError>
                      {errors.urls?.[index]?.value?.message || msg('metrics.targetUrls.requiredShort')}
                    </FieldError>
                  )}
                </TextField>
              </div>
              <KindHint control={control} index={index} />
            </div>
          </FieldGroup>
        </div>
      ))}
      <span className={styles.addTargetUrl}>
        <Button
          aria-label={msg('metrics.targetUrls.addUrlLabel')}
          onPress={() => append({ kind: 'canonical', value: '' })}
          className={styles.noBorderIcon}
          isDisabled={isSaving || !access.isAllowed}
        >
          <Icon name="add-circle" size="small" />
          <span className={styles.addTargetUrlText}>
            <Message id="metrics.targetUrls.addTargetUrl" />
          </span>
        </Button>
      </span>
      <Alert isOpen={!access.isAllowed} className={styles.noAccessAlert}>
        <Text>{access.getModifyFieldReason()}</Text>
      </Alert>
    </div>
  );
}

function validate(value: string, formValues: MetricForm, index: number): boolean | string {
  const kind = formValues.urls?.[index].kind;
  //TODO: Check for duplicates and alert

  switch (kind) {
    // TODO the backend doesn't validate this, do we really need to?
    case 'regex':
    case 'substring':
      //substring/pattern is not empty
      return value.length > 0 || msg('metrics.targetUrls.required');
    case 'canonical':
    case 'exact':
      //isNotEmpty
      //isAbsoluteURL
      return (isAbsoluteUrl(value) && value.length > 0) || msg('metrics.targetUrls.absoluteUrlRequired');
    default:
      return !!value || msg('metrics.targetUrls.requiredShort');
  }
}

function KindHint({ control, index }: { control: Control<MetricForm>; index: number }) {
  const kind = useWatch({ control, name: `urls.${index}.kind` });
  return (
    <Text slot="subtitle" className={styles.kindHint}>
      {formHint(kind)}
    </Text>
  );
}
